import { z } from 'zod';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
import { v1PlatformIdFilterDescription, v1PlatformSchema } from './vocabulary';
/**
 * Public IDE-extension inventory contract for `/api/v1` (KIR-4431, slice 1).
 *
 * Field names follow `apps/backend/src/routes/v1/NAMING.md`. The internal shape
 * this adapts (`ExtensionListItem`) is not re-exported here — the DTO layer in
 * `routes/v1/dto/extensions.ts` is the only place the two meet.
 *
 * **A note on the internal vocabulary**, because it is actively misleading and
 * the public names deliberately diverge. Internally, the row field `sources` is
 * the list of IDEs an extension was found in, the `ide` filter filters that
 * list, and the `source` filter is something else entirely: built-in versus
 * added. A public API that passed those through would ship a `source` filter and
 * a `sources` field that mean different things. Here the IDE list is
 * `platforms` — the same word the MCP-server and alert contracts use for the
 * same concept — and `source` keeps the screen's meaning of built-in vs added.
 */
/**
 * Where the extension came from.
 *
 * The two values the Inventory screen's "Source" column shows, and the two the
 * internal filter accepts. `built-in` means the IDE ships it; `added` means
 * someone installed it.
 */
export const v1ExtensionSourceSchema = z.enum(['built-in', 'added']).meta({
    id: 'V1ExtensionSource',
    title: 'Extension source',
    description: 'Whether the IDE ships this extension itself (`built-in`) or someone installed it (`added`).',
});
/** One version of an extension, with how many developers are on it. */
export const v1ExtensionVersionSchema = z
    .object({
    version: z.string().describe('The version string, as reported.'),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users are on this version.'),
    firstSeenAt: z
        .string()
        .datetime()
        .describe('When this version was first reported, ISO 8601 UTC.'),
})
    .meta({
    id: 'V1ExtensionVersion',
    title: 'Extension version',
    description: 'One reported version of an extension, and its install count.',
    examples: [
        {
            version: '2024.14.1',
            userCount: 18,
            firstSeenAt: '2026-06-02T08:41:19Z',
        },
    ],
});
/**
 * The extension's security posture, when it has been assessed.
 *
 * The same two values the public alert contract publishes for a finding, so a
 * caller can rank an extension against an alert without a second vocabulary.
 */
export const v1ExtensionSecuritySchema = z
    .object({
    severity: z
        .enum(['low', 'medium', 'high', 'critical'])
        .describe("How serious the extension's worst finding is. The product's four levels."),
    score: z
        .number()
        .int()
        .nullable()
        .describe('Risk score, 0-100, or null when no score was computed.'),
})
    .meta({
    id: 'V1ExtensionSecurity',
    title: 'Extension security',
    description: 'The security findings summary for an extension.',
    examples: [{ severity: 'medium', score: 47 }],
});
/**
 * One IDE extension in the organization's inventory.
 *
 * A row is an *extension*, aggregated across every developer, machine and IDE
 * that reported it — which is why `versions` and `platforms` are arrays and the
 * counts are counts. It is not one installation.
 */
export const v1ExtensionSchema = z
    .object({
    id: z
        .string()
        .describe('Opaque identifier for this extension within your organization. Stable, and safe to store and pass back to the by-ID and users endpoints. Do not parse it: its internal format is not part of this contract and may change. In particular it is **not** the bare marketplace ID — one marketplace ID can name several rows here, so the identifier covers the whole of what makes a row distinct. The marketplace ID, publisher and name are published as their own fields.'),
    name: z
        .string()
        .describe("The extension's own name, without the publisher prefix — for example `python`."),
    displayName: z
        .string()
        .nullable()
        .describe('The human-readable name the extension presents, when it reports one. Null when it does not, in which case `name` is what the Inventory screen shows.'),
    publisher: z
        .string()
        .describe('The publisher, as the marketplace identifies them.'),
    versions: z
        .array(v1ExtensionVersionSchema)
        .describe('Every distinct version seen across your endpoint users, with the number of endpoint users on each.'),
    platforms: z
        .array(v1PlatformSchema)
        .describe('The IDEs and coding agents this extension was found in, each with the display name the Kirin app shows. Pass `platforms[].id` to the `platform` filter.'),
    source: v1ExtensionSourceSchema,
    verifiedPublisher: z
        .boolean()
        .describe('Whether the publisher is one Kirin treats as verified. False is not a finding on its own — most publishers are not on that list.'),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users have this extension, across all versions.'),
    security: v1ExtensionSecuritySchema
        .nullable()
        .describe('The security findings summary, or null when there is none. Null covers both "assessed and nothing found" and "not assessed" — including an organization without extension security scanning enabled — so it is not evidence that the extension is clean.'),
    firstSeenAt: z
        .string()
        .datetime()
        .describe('When this extension was first reported, ISO 8601 UTC.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe('The most recent report of any install of this extension, ISO 8601 UTC.'),
})
    .meta({
    id: 'V1Extension',
    title: 'Extension',
    description: "One IDE extension in your organization's inventory, aggregated across every endpoint user, machine and IDE that reported it.",
    examples: [
        {
            id: 'WyJtcy1weXRob24ucHl0aG9uIiwibXMtcHl0aG9uIiwicHl0aG9uIl0',
            name: 'python',
            displayName: 'Python',
            publisher: 'ms-python',
            versions: [
                {
                    version: '2024.14.1',
                    userCount: 18,
                    firstSeenAt: '2026-06-02T08:41:19Z',
                },
                {
                    version: '2024.12.3',
                    userCount: 4,
                    firstSeenAt: '2026-05-11T13:07:55Z',
                },
            ],
            platforms: [{ id: 'vscode', name: 'VS Code' }],
            source: 'added',
            verifiedPublisher: true,
            userCount: 22,
            security: { severity: 'medium', score: 47 },
            firstSeenAt: '2026-05-11T13:07:55Z',
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/**
 * Sortable fields, named for the public field they sort.
 *
 * `riskEstimate` is deliberately absent: the AgentMesh risk estimate is not a
 * published field on this resource, and a sort key over something a caller
 * cannot read is not usable.
 */
export const v1ExtensionSortFieldSchema = z
    .enum([
    'name',
    'publisher',
    'source',
    'platform',
    'userCount',
    'security',
    'firstSeenAt',
    'lastSeenAt',
])
    .describe('Which field to sort by.');
/**
 * The list query.
 *
 * Every filter is optional and ANDed with the rest; a comma-separated filter ORs
 * within itself. The set matches the filters the Inventory → Extensions screen
 * offers, per the ticket: name, source, publisher and IDE — the last of which is
 * `platform` here, for the reason given at the top of this file.
 *
 * Deliberately unnamed (no `.meta({ id })`): a named schema is emitted as a
 * `$ref`, and `@fastify/swagger` cannot expand a `$ref` into a parameter list.
 */
export const v1ExtensionListQuerySchema = v1PaginationQuerySchema.extend({
    name: csvParam('Filter by name, matching the name shown on the screen.'),
    publisher: csvParam('Filter by publisher.'),
    source: csvParam('Filter by source (`built-in`, `added`).'),
    platform: csvParam(`Filter by the IDE or coding agent the extension was found in. ${v1PlatformIdFilterDescription}`),
    sortBy: v1ExtensionSortFieldSchema.default('name'),
    sortOrder: v1SortOrderSchema.default('asc'),
});
export const v1ExtensionListResponseSchema = z
    .object({
    data: z.array(v1ExtensionSchema).describe('This page of extensions.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1ExtensionList',
    title: 'Extension list',
    description: "A page of the IDE extensions installed across your organization's endpoint users.",
    examples: [
        {
            data: [
                {
                    id: 'WyJtcy1weXRob24ucHl0aG9uIiwibXMtcHl0aG9uIiwicHl0aG9uIl0',
                    name: 'python',
                    displayName: 'Python',
                    publisher: 'ms-python',
                    versions: [
                        {
                            version: '2024.14.1',
                            userCount: 18,
                            firstSeenAt: '2026-06-02T08:41:19Z',
                        },
                        {
                            version: '2024.12.3',
                            userCount: 4,
                            firstSeenAt: '2026-05-11T13:07:55Z',
                        },
                    ],
                    platforms: [{ id: 'vscode', name: 'VS Code' }],
                    source: 'added',
                    verifiedPublisher: true,
                    userCount: 22,
                    security: { severity: 'medium', score: 47 },
                    firstSeenAt: '2026-05-11T13:07:55Z',
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 148,
                totalPages: 6,
                hasMore: true,
            },
        },
    ],
});
/**
 * One developer who has a given extension installed.
 *
 * `id` is the same identifier the inventory-users resource publishes, so a
 * caller can join the two.
 */
export const v1ExtensionUserSchema = z
    .object({
    id: z
        .string()
        .describe('The endpoint user, as identified in the inventory-users resource. Join on this to read their full inventory record.'),
    name: z.string().nullable().describe('Display name, if known.'),
    email: z.string().nullable().describe('Email address, if known.'),
    version: z
        .string()
        .describe('The version this endpoint user currently has installed.'),
    platforms: z
        .array(v1PlatformSchema)
        .describe('The IDEs and coding agents in which this endpoint user has the extension.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe("This endpoint user's most recent report, ISO 8601 UTC."),
})
    .meta({
    id: 'V1ExtensionUser',
    title: 'Extension user',
    description: 'An endpoint user who has a given extension installed.',
    examples: [
        {
            id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
            name: 'Stella Normal',
            email: 'stella@example.com',
            version: '2024.14.1',
            platforms: [{ id: 'vscode', name: 'VS Code' }],
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/**
 * The users of one extension.
 *
 * Not paginated, for the same reason as the MCP-server users list: the response
 * is bounded by the developers in one organization who have one extension, and
 * the internal handler returns the set in one query. A `pagination` object would
 * promise a walk this resource does not implement.
 */
export const v1ExtensionUserListResponseSchema = z
    .object({
    data: z
        .array(v1ExtensionUserSchema)
        .describe('Every endpoint user who has this extension installed.'),
})
    .meta({
    id: 'V1ExtensionUserList',
    title: 'Extension users',
    description: 'Every endpoint user in your organization who has a given extension installed. Not paginated.',
    examples: [
        {
            data: [
                {
                    id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
                    name: 'Stella Normal',
                    email: 'stella@example.com',
                    version: '2024.14.1',
                    platforms: [{ id: 'vscode', name: 'VS Code' }],
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
        },
    ],
});
//# sourceMappingURL=extensions.js.map